<template>
  <b-modal
    id="device-modal"
    ref="deviceModal"
    size="sm"
    :title="$t('regional.exam.connectDevice')"
    header-text-variant="light"
    header-class="border-bottom-0"
    header-bg-variant="primary"
    footer-class="border-top-0 pt-0"
    centered
    no-close-on-backdrop
  >
    <div>
      <div class="text-center" style="font-size: 1rem; min-height: 106px">
        <div
          v-if="state === 'scanning' || state === 'connecting'"
          class="d-flex flex-column justify-content-center align-items-center"
          style="min-height: 106px"
        >
          <b-spinner
            variant="primary"
            :label="$t('app.common.loading')"
          ></b-spinner>
          <p class="mt-3 mb-0">
            {{
              state === 'scanning'
                ? $t('app.common.deviceSearching')
                : $t('app.common.deviceCommunicating')
            }}
          </p>
        </div>

        <!-- Solo con più dispositivi in zona: con uno solo la connessione parte
             da sé (scan con autoSelect). -->
        <div v-if="state === 'selecting'" style="min-height: 106px">
          <p class="mb-2">{{ $t('app.common.deviceChoose') }}</p>
          <b-list-group>
            <b-list-group-item
              v-for="device in foundDevices"
              :key="device.deviceId"
              button
              class="text-left"
              @click="selectDevice(device.deviceId)"
            >
              {{ device.deviceName || device.deviceId }}
            </b-list-group-item>
          </b-list-group>
          <!-- Il dispositivo dell'utente può essere uno vecchio (seriale) con
               quelli di altri in zona: è la sua via d'uscita. -->
          <b-button
            variant="link"
            size="sm"
            class="mt-2"
            :disabled="usingSerial"
            @click="useSerial"
          >
            {{ $t('app.common.deviceNotListed') }}
          </b-button>
        </div>

        <div
          v-if="state === 'done'"
          class="d-flex flex-column justify-content-center align-items-center"
          style="min-height: 106px"
        >
          <p class="mb-1">
            Rz: <span id="rz-span">{{ rz }}</span> &#937;
          </p>
          <p class="mb-0">
            Xc: <span id="xc-span">{{ xc }}</span> &#937;
          </p>
        </div>

        <div
          v-if="state === 'error'"
          class="text-danger d-flex justify-content-center align-items-center"
          style="min-height: 106px"
        >
          {{ errorMessage }}
        </div>

        <!-- Dispositivo non pronto / schermata errata: chiesto qui dentro e non
             con un msgBox sopra, che lascerebbe due modali aperte insieme. -->
        <div
          v-if="state === 'prompt'"
          class="d-flex flex-column justify-content-center align-items-center"
          style="min-height: 106px"
        >
          <p class="font-weight-bold mb-1">{{ prompt.title }}</p>
          <p class="mb-0">{{ prompt.message }}</p>
        </div>
      </div>
    </div>
    <div slot="modal-footer" class="w-100">
      <div class="float-right">
        <!-- Sempre attivo: la ricerca sul seriale può durare ~40 s. Chiudendo,
             flowId scarta qualunque risposta arrivi dopo. -->
        <b-button variant="outline-secondary" @click="closeModal">
          {{
            state === 'prompt'
              ? $t('app.modals.cancel')
              : $t('fitness.modal.close')
          }}
        </b-button>
        <b-button
          v-if="state === 'prompt'"
          variant="primary"
          class="ml-1"
          @click="answerPrompt(true)"
        >
          {{ $t('app.common.retry') }}
        </b-button>
        <b-button
          v-else-if="state === 'error'"
          variant="primary"
          class="ml-1"
          @click="startFlow"
        >
          {{ $t('app.common.retry') }}
        </b-button>
        <b-button
          v-else
          :disabled="state !== 'done'"
          variant="primary"
          class="ml-1"
          @click="handleSubmit"
        >
          {{ $t('app.forms.insert') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  serialFailure,
  userError,
  withSerialTimeout,
} from '~/utils/deviceErrors'

export default {
  data() {
    return {
      state: 'scanning',
      // Incrementato a ogni avvio e a ogni chiusura: un flusso che non trova più
      // il suo numero è stato abbandonato e non tocca la modale.
      flowId: 0,
      usingSerial: false,
      rz: null,
      xc: null,
      errorMessage: '',
      prompt: null, // { title, message } while state === 'prompt'
    }
  },

  computed: {
    // Populated by the store when Electron's main process emits ble:devicesFound.
    // Empty on Web — the browser shows its own native picker.
    foundDevices() {
      return this.$store.state.bluetooth.foundDevices
    },
  },

  watch: {
    // Con un solo dispositivo lo sceglie lo store (autoSelect): la lista serve
    // solo a distinguerne più d'uno.
    foundDevices(devices) {
      if (devices.length > 1 && this.state === 'scanning') {
        this.state = 'selecting'
      }
    },
  },

  mounted() {
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === 'device-modal') this.startFlow()
    })
    this.$root.$on('bv::modal::hide', (bvEvent, modalId) => {
      if (modalId !== 'device-modal') return
      // Invalida il flusso in corso: una risposta tardiva del seriale non deve
      // più scrivere Rz/Xc nel form.
      this.flowId++
      clearTimeout(this.submitTimer)
      this.answerPrompt(false)
      this.$store.dispatch('bluetooth/disconnect')
    })
  },

  destroyed() {
    this.$root.$off('bv::modal::show')
    this.$root.$off('bv::modal::hide')
  },

  methods: {
    closeModal() {
      this.$refs.deviceModal.hide()
    },

    handleSubmit() {
      document.querySelector('#rz-input').value = this.rz
      document.querySelector('#rz-input').dispatchEvent(new Event('input'))
      document.querySelector('#xc-input').value = this.xc
      document.querySelector('#xc-input').dispatchEvent(new Event('input'))
      this.closeModal()
    },

    // Shows a not-ready / wrong-screen message in place and waits for Retry
    // (true) or Cancel / closing the modal (false).
    askRetry(status) {
      const notReady = status === 'notReady'
      this.prompt = {
        title: this.$t(
          notReady
            ? 'app.common.deviceNotReady'
            : 'app.common.deviceWrongScreen'
        ),
        message: this.$t(
          notReady
            ? 'app.common.deviceNotReadyMessage'
            : 'app.common.deviceWrongScreenMessage'
        ),
      }
      this.state = 'prompt'
      return new Promise((resolve) => {
        this.promptResolve = resolve
      })
    },

    answerPrompt(retry) {
      const resolve = this.promptResolve
      if (!resolve) return
      this.promptResolve = null
      if (retry) this.state = 'connecting'
      resolve(retry)
    },

    showResults() {
      this.state = 'done'
      this.submitTimer = setTimeout(() => this.handleSubmit(), 1500)
    },

    // Electron only: forwards the selected device ID to the main process so it
    // can resolve the pending requestDevice() call.
    selectDevice(deviceId) {
      this.$store.dispatch('bluetooth/selectDevice', deviceId)
    },

    // Passaggio al seriale: automatico se non c'è nessun dispositivo BLE, a mano
    // dalla lista ("non è nell'elenco"). Annulla il picker BLE eventualmente
    // ancora aperto, poi esegue il flusso seriale. Il flag protegge dal doppio
    // avvio e dice ai rami di errore BLE di non sovrascrivere quello che sta
    // facendo il seriale.
    async useSerial() {
      if (this.usingSerial) return
      this.usingSerial = true

      await this.$store.dispatch('bluetooth/disconnect')
      return this.runSerial(this.flowId)
    },

    // Fallback: nessun dispositivo BLE in giro, si usa il gateway seriale.
    // Il gateway non dice quando trova il dispositivo, quindi per tutta l'attesa
    // si resta in 'scanning' ("Ricerca del dispositivo").
    // Il body della risposta è HTML (lo stesso che la modale seriale inietta e
    // poi rilegge dal DOM), quindi Rz/Xc si estraggono senza renderizzarlo.
    async runSerial(flow) {
      this.state = 'scanning'

      let response
      try {
        response = await withSerialTimeout(
          this.$ipc('connectDevice', {
            locale: this.$i18n.localeProperties.code,
          })
        )
      } catch (err) {
        if (flow !== this.flowId) return

        const { status, error } = serialFailure(err, (key) => this.$t(key))
        if (!status) return this.showError(error)
        if (await this.askRetry(status)) return this.runSerial(flow)
        return this.closeModal()
      }

      if (flow !== this.flowId) return

      const body = new DOMParser().parseFromString(
        (response.result && response.result.body) || '',
        'text/html'
      )
      const value = (id) => {
        const node = body.querySelector(`#${id}`)
        return node ? parseFloat(node.textContent) : null
      }

      this.rz = value('rz-span')
      this.xc = value('xc-span')

      if (this.rz === null || this.xc === null)
        return this.showError(
          userError(this.$t('app.common.deviceRecognitionError'))
        )

      this.showResults()
    },

    // Gli errori di protocollo/GATT ("GATT operation failed", "Device did not
    // respond", …) non dicono nulla a chi usa l'app: restano in console e a
    // video va un messaggio generico.
    showError(err) {
      console.error(err)
      this.errorMessage = err.userFacing
        ? err.message
        : this.$t('app.common.deviceCommunicationError')
      this.state = 'error'
    },

    async startFlow() {
      const flow = ++this.flowId
      this.state = 'scanning'
      this.usingSerial = false
      this.rz = null
      this.xc = null
      this.errorMessage = ''

      try {
        // 1. Open picker.
        //    Electron: ble:devicesFound populates foundDevices → watcher flips to
        //    'selecting' → user clicks a device → selectDevice() → scan resolves.
        //    Web: browser native picker resolves directly.
        //    Se entro il timeout non compare nessun dispositivo si ripiega
        //    silenziosamente sul seriale.
        try {
          await this.$store.dispatch('bluetooth/scan', { autoSelect: true })
        } catch (err) {
          if (err.code === 'NO_DEVICE' && this.$ipc) return this.useSerial()
          throw err
        }

        // 2. Open GATT, get characteristic, start notifications.
        this.state = 'connecting'
        await this.$store.dispatch('bluetooth/connect')

        // 3. Identify the device — abort if not recognized.
        const { recognition } = await this.$store.dispatch(
          'bluetooth/recognize'
        )
        //    Shown inline like every other BLE error: a msgBox on top of this
        //    modal would report it twice.
        if (recognition === 'notOk')
          throw userError(this.$t('app.common.deviceRecognitionError'))

        // 4. Sync the device clock.
        await this.$store.dispatch('bluetooth/setDate', new Date())

        // 5. Measurement loop — retry on notReady / wrongScreen, abort on cancel.
        let measurement
        do {
          measurement = await this.$store.dispatch('bluetooth/totalMeasure')

          if (measurement.status !== 'ok') {
            // Cancel / closing: the modal is already going away, @hide disconnects.
            if (!(await this.askRetry(measurement.status)))
              return this.closeModal()
          }
        } while (measurement.status !== 'ok')

        // 6. Populate form values.
        this.rz = measurement.resistance
        this.xc = measurement.reactance

        // 7. Tell the device to power off after the session.
        await this.$store.dispatch('bluetooth/deviceAutoOff')

        // 8. Close the GATT connection.
        await this.$store.dispatch('bluetooth/disconnect')

        if (flow === this.flowId) this.showResults()
      } catch (err) {
        // Lo scan può rifiutare perché l'utente è passato al seriale a mano, o
        // perché ha chiuso la modale: in entrambi i casi il flusso non è più
        // questo e non va sovrascritto.
        if (this.usingSerial || flow !== this.flowId) return

        this.showError(err)
        await this.$store.dispatch('bluetooth/disconnect')
      }
    },
  },
}
</script>
